


import { Fragment, useMemo, useRef, useState, useEffect } from "react";
import { adminLeaveReportApi } from "../../app/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FileSearch,
  Filter,
  User,
  Calendar,
  FileSpreadsheet,
  FileText,
  Printer,
  RotateCcw,
  ChevronRight,
  ChevronDown,
  LayoutList,
  Search
} from "lucide-react";
/* DATE FORMATTER */
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getMonthRange = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  return {
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
  };
};

const formatMonth = (date) =>
  date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const normalizeStatus = (status) => {
  if (Number(status) === 1) return "Approved";
  if (Number(status) === 0) return "Rejected";
  return "Pending";
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const downloadFile = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const cleanPdfText = (value) =>
  String(value ?? "-")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "-";

const escapePdfText = (value) =>
  cleanPdfText(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const wrapPdfText = (value, maxChars) => {
  const words = cleanPdfText(value).split(" ");
  const lines = [];
  let line = "";

  words.forEach((word) => {
    if ((line ? `${line} ${word}` : word).length > maxChars) {
      if (line) lines.push(line);
      line = word.slice(0, maxChars);
    } else {
      line = line ? `${line} ${word}` : word;
    }
  });

  if (line) lines.push(line);
  return lines.length ? lines : ["-"];
};

const createLeaveReportPdf = ({ rows, monthLabel, employeeCount }) => {
  const pageWidth = 842;
  const pageHeight = 595;
  const margin = 28;
  const headerHeight = 78;
  const footerHeight = 28;
  const rowPadding = 7;
  const lineHeight = 9;
  const columns = [
    { key: "employee", label: "Employee", x: 28, width: 92, chars: 18 },
    { key: "category", label: "Category", x: 120, width: 88, chars: 17 },
    { key: "start", label: "Start", x: 208, width: 62, chars: 11 },
    { key: "end", label: "End", x: 270, width: 62, chars: 11 },
    { key: "duration", label: "Duration", x: 332, width: 56, chars: 9 },
    { key: "reason", label: "Reason", x: 388, width: 190, chars: 39 },
    { key: "remarks", label: "Remarks", x: 578, width: 164, chars: 32 },
    { key: "status", label: "Decision", x: 742, width: 72, chars: 11 },
  ];
  const tableRight = columns[columns.length - 1].x + columns[columns.length - 1].width;
  const pages = [];
  let pageRows = [];
  let y = pageHeight - headerHeight - 22;

  rows.forEach((row) => {
    const wrapped = columns.reduce((acc, column) => {
      acc[column.key] = wrapPdfText(row[column.key], column.chars).slice(0, 4);
      return acc;
    }, {});
    const rowHeight = Math.max(28, Math.max(...Object.values(wrapped).map((lines) => lines.length)) * lineHeight + rowPadding * 2);

    if (y - rowHeight < footerHeight) {
      pages.push(pageRows);
      pageRows = [];
      y = pageHeight - headerHeight - 22;
    }

    pageRows.push({ wrapped, rowHeight });
    y -= rowHeight;
  });

  if (pageRows.length) pages.push(pageRows);

  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };

  const fontObjectId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const boldFontObjectId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");
  const pageObjectIds = [];

  const text = (x, yPos, value, size = 8, font = "F1") =>
    `BT /${font} ${size} Tf ${x} ${yPos} Td (${escapePdfText(value)}) Tj ET\n`;
  const line = (x1, y1, x2, y2) => `${x1} ${y1} m ${x2} ${y2} l S\n`;

  pages.forEach((rowsForPage, pageIndex) => {
    let content = "";
    content += "0.96 0.97 1 rg 0 0 842 595 re f\n";
    content += "0.12 0.09 0.29 rg 0 538 842 57 re f\n";
    content += "1 1 1 rg ";
    content += text(margin, 564, "Leave Report", 18, "F2");
    content += "0.82 0.85 0.92 rg ";
    content += text(margin, 546, `${monthLabel} - ${rows.length} leave records - ${employeeCount} employees`, 9);

    let currentY = pageHeight - headerHeight;
    content += "0.12 0.09 0.29 rg ";
    content += `${margin} ${currentY - 18} ${tableRight - margin} 22 re f\n`;
    content += "1 1 1 rg ";
    columns.forEach((column) => {
      content += text(column.x + 4, currentY - 10, column.label, 7, "F2");
    });
    currentY -= 22;

    rowsForPage.forEach(({ wrapped, rowHeight }, rowIndex) => {
      content += rowIndex % 2 === 0 ? "1 1 1 rg " : "0.95 0.96 0.99 rg ";
      content += `${margin} ${currentY - rowHeight} ${tableRight - margin} ${rowHeight} re f\n`;
      content += "0.83 0.86 0.92 RG ";
      content += line(margin, currentY - rowHeight, tableRight, currentY - rowHeight);
      content += "0.13 0.16 0.23 rg ";

      columns.forEach((column) => {
        wrapped[column.key].forEach((lineText, lineIndex) => {
          content += text(column.x + 4, currentY - rowPadding - 7 - lineIndex * lineHeight, lineText, 7);
        });
      });

      currentY -= rowHeight;
    });

    content += "0.39 0.45 0.55 rg ";
    content += text(margin, 18, `Generated ${new Date().toLocaleDateString("en-GB")}`, 8);
    content += text(pageWidth - 70, 18, `Page ${pageIndex + 1} of ${pages.length}`, 8);

    const streamId = addObject(`<< /Length ${content.length} >>\nstream\n${content}endstream`);
    const pageId = addObject(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontObjectId} 0 R /F2 ${boldFontObjectId} 0 R >> >> /Contents ${streamId} 0 R >>`);
    pageObjectIds.push(pageId);
  });

  const pagesObjectId = addObject(`<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageObjectIds.length} >>`);
  pageObjectIds.forEach((pageId) => {
    objects[pageId - 1] = objects[pageId - 1].replace("/Parent 0 0 R", `/Parent ${pagesObjectId} 0 R`);
  });
  const catalogObjectId = addObject(`<< /Type /Catalog /Pages ${pagesObjectId} 0 R >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogObjectId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return pdf;
};
export default function LeaveReport() {
  const navigate = useNavigate();
  const currentMonthRange = getMonthRange();
  const [report, setReport] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [expandedEmployees, setExpandedEmployees] = useState({});

  const [filters, setFilters] = useState({
    status: "",
    start_date: currentMonthRange.start,
    end_date: currentMonthRange.end,
    leave_type: "",
    search: "",
  });
  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };
  const handleReset = () => {
    const range = getMonthRange();
    setSelectedMonth(new Date());
    setFilters({
      status: "",
      start_date: range.start,
      end_date: range.end,
      leave_type: "",
      search: "",
    });
    setExpandedEmployees({});
  };

  const handleMonthChange = (date) => {
    if (!date) return;
    const range = getMonthRange(date);
    setSelectedMonth(date);
    setFilters((current) => ({
      ...current,
      start_date: range.start,
      end_date: range.end,
    }));
    setExpandedEmployees({});
  };
  const loadReport = async () => {
    setIsLoading(true);
    const res = await adminLeaveReportApi();
    if (res.success) {
      setReport(res.data || []);
    } else {
      toast.error("Failed to load report");
    }
    setIsLoading(false);
  };
  useEffect(() => {
    loadReport();
  }, []);
  const filteredReport = report.filter((r) => {
    const matchesSearch = filters.search === "" || r.employee_name?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesLeaveType = filters.leave_type === "" || r.leave_type === filters.leave_type;
    const matchesStatus =
      filters.status === "" ||
      (filters.status === "pending"
        ? Number(r.approval_status) !== 1 && Number(r.approval_status) !== 0
        : Number(r.approval_status) === Number(filters.status));
    const matchesStartDate = filters.start_date === "" || r.leave_start_date >= filters.start_date;
    const matchesEndDate = filters.end_date === "" || r.leave_end_date <= filters.end_date;

    return matchesSearch && matchesLeaveType && matchesStatus && matchesStartDate && matchesEndDate;
  });

  const groupedReport = useMemo(() => {
    const groups = new Map();

    filteredReport.forEach((entry) => {
      const key = entry.employee_id || entry.employee_name || entry.id;
      const existing = groups.get(key) || {
        key,
        employee_id: entry.employee_id,
        employee_name: entry.employee_name,
        entries: [],
        totalDuration: 0,
      };

      existing.entries.push(entry);
      existing.totalDuration += Number(entry.duration) || 0;
      groups.set(key, existing);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        entries: group.entries.sort(
          (a, b) => new Date(b.leave_start_date).getTime() - new Date(a.leave_start_date).getTime()
        ),
      }))
      .sort((a, b) => (a.employee_name || "").localeCompare(b.employee_name || ""));
  }, [filteredReport]);

  const exportRows = groupedReport.flatMap((group) =>
    group.entries.map((entry) => ({
      employee: group.employee_name || "-",
      category: entry.leave_type === "Unpaid Leave" ? "Leave Without Pay [LWP]" : entry.leave_type || "-",
      start: formatDate(entry.leave_start_date),
      end: formatDate(entry.leave_end_date),
      duration: `${entry.duration || 0} days`,
      reason: entry.employee_comment || "-",
      remarks: entry.remarks || "-",
      status: normalizeStatus(entry.approval_status),
    }))
  );

  const reportFileName = `Leave_Report_${formatMonth(selectedMonth).replace(/\s+/g, "_")}`;

  const handleExportExcel = () => {
    if (exportRows.length === 0) {
      toast.info("No leave records to export");
      return;
    }

    const headers = ["Employee", "Category", "Period Start", "Period End", "Duration", "Reason", "Remarks", "Decision"];
    const csvRows = [
      headers.join(","),
      ...exportRows.map((row) =>
        [row.employee, row.category, row.start, row.end, row.duration, row.reason, row.remarks, row.status]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];

    downloadFile(csvRows.join("\n"), `${reportFileName}.csv`, "text/csv;charset=utf-8;");
    toast.success("Excel report downloaded");
  };

  const handleExportPdf = () => {
    if (exportRows.length === 0) {
      toast.info("No leave records to export");
      return;
    }

    const pdf = createLeaveReportPdf({
      rows: exportRows,
      monthLabel: formatMonth(selectedMonth),
      employeeCount: groupedReport.length,
    });

    downloadFile(pdf, `${reportFileName}.pdf`, "application/pdf");
    toast.success("PDF report downloaded");
  };

  const handlePrintReport = () => {
    if (exportRows.length === 0) {
      toast.info("No leave records to print");
      return;
    }

    const tableRows = exportRows
      .map(
        (row) => `
          <tr>
            <td>${escapeHtml(row.employee)}</td>
            <td>${escapeHtml(row.category)}</td>
            <td>${escapeHtml(row.start)}</td>
            <td>${escapeHtml(row.end)}</td>
            <td>${escapeHtml(row.duration)}</td>
            <td>${escapeHtml(row.reason)}</td>
            <td>${escapeHtml(row.remarks)}</td>
            <td>${escapeHtml(row.status)}</td>
          </tr>`
      )
      .join("");

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      toast.error("Please allow popups to export PDF");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(reportFileName)}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #1f2937; padding: 24px; }
            h1 { margin: 0; font-size: 24px; }
            p { margin: 6px 0 20px; color: #64748b; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th { background: #1e174a; color: white; text-align: left; }
            th, td { border: 1px solid #d8d8e2; padding: 8px; vertical-align: top; }
            tr:nth-child(even) { background: #f8fafc; }
          </style>
        </head>
        <body>
          <h1>Leave Report</h1>
          <p>${escapeHtml(formatMonth(selectedMonth))} • ${exportRows.length} leave records • ${groupedReport.length} employees</p>
          <table>
            <thead>
              <tr>
                <th>Employee</th><th>Category</th><th>Period Start</th><th>Period End</th>
                <th>Duration</th><th>Reason</th><th>Remarks</th><th>Decision</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    toast.success("Print view opened");
  };

  const toggleEmployee = (employeeId) => {
    setExpandedEmployees((current) => ({
      ...current,
      [employeeId]: !current[employeeId],
    }));
  };
  const getStatusBadge = (status) => {
    if (status === 1)
      return <span className="px-2.5 py-1 rounded-lg text-[9px] font-semibold uppercase tracking-widest bg-green-500/10 border border-green-500/20 text-green-400">Approved</span>;
    if (status === 0)
      return <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-red-500/10 border border-red-500/20 text-red-400">Rejected</span>;
    return <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-yellow-500/10 border border-yellow-500/20 text-yellow-500">Pending</span>;
  };
  return (
    <>
      <style>
        {`
          .leave-report-datepicker {
            border: 1px solid rgba(255,255,255,0.16) !important;
            border-radius: 20px !important;
            overflow: hidden !important;
            background: linear-gradient(145deg, #120d2c 0%, #1b1140 62%, #25164f 100%) !important;
            color: #f8fafc !important;
            font-family: inherit !important;
            box-shadow: 0 24px 70px rgba(4,2,18,0.55), inset 0 1px 0 rgba(255,255,255,0.10) !important;
          }
          .leave-report-datepicker .react-datepicker__triangle {
            display: none !important;
          }
          .leave-report-datepicker .react-datepicker__header {
            background: rgba(255,255,255,0.06) !important;
            border-bottom: 1px solid rgba(255,255,255,0.10) !important;
            padding-top: 12px !important;
          }
          .leave-report-datepicker .react-datepicker__current-month,
          .leave-report-datepicker .react-datepicker-time__header,
          .leave-report-datepicker .react-datepicker-year-header,
          .leave-report-datepicker .react-datepicker__day-name,
          .leave-report-datepicker .react-datepicker__day,
          .leave-report-datepicker .react-datepicker__month-text {
            color: rgba(255,255,255,0.82) !important;
            font-weight: 700 !important;
          }
          .leave-report-datepicker .react-datepicker__day-name {
            color: rgba(199,210,254,0.72) !important;
            font-size: 11px !important;
          }
          .leave-report-datepicker .react-datepicker__day,
          .leave-report-datepicker .react-datepicker__month-text {
            border-radius: 12px !important;
          }
          .leave-report-datepicker .react-datepicker__day:hover,
          .leave-report-datepicker .react-datepicker__month-text:hover {
            background: rgba(129,140,248,0.22) !important;
            color: #fff !important;
          }
          .leave-report-datepicker .react-datepicker__day--outside-month {
            color: rgba(255,255,255,0.28) !important;
          }
          .leave-report-datepicker .react-datepicker__day--selected,
          .leave-report-datepicker .react-datepicker__day--keyboard-selected,
          .leave-report-datepicker .react-datepicker__month-text--selected,
          .leave-report-datepicker .react-datepicker__month-text--keyboard-selected {
            background: linear-gradient(135deg, #a5b4fc, #60a5fa) !important;
            color: #0f0c29 !important;
            box-shadow: 0 10px 24px rgba(96,165,250,0.24) !important;
          }
          .leave-report-datepicker .react-datepicker__navigation-icon::before,
          .leave-report-datepicker .react-datepicker__month-read-view--down-arrow,
          .leave-report-datepicker .react-datepicker__year-read-view--down-arrow {
            border-color: rgba(255,255,255,0.92) !important;
          }
          .leave-report-datepicker .react-datepicker__month-read-view,
          .leave-report-datepicker .react-datepicker__year-read-view {
            border: 1px solid rgba(255,255,255,0.12) !important;
            border-radius: 999px !important;
            padding: 5px 10px !important;
            background: rgba(255,255,255,0.08) !important;
          }
          .leave-report-datepicker .react-datepicker__month-read-view--selected-month,
          .leave-report-datepicker .react-datepicker__year-read-view--selected-year {
            color: #fff !important;
            font-weight: 900 !important;
          }
          .leave-report-datepicker .react-datepicker__month-dropdown,
          .leave-report-datepicker .react-datepicker__year-dropdown {
            border: 1px solid rgba(255,255,255,0.14) !important;
            border-radius: 14px !important;
            background: #1b1140 !important;
            color: white !important;
            overflow: hidden !important;
          }
          .leave-report-datepicker .react-datepicker__month-option,
          .leave-report-datepicker .react-datepicker__year-option {
            color: rgba(255,255,255,0.82) !important;
            padding: 8px 10px !important;
          }
          .leave-report-datepicker .react-datepicker__month-option:hover,
          .leave-report-datepicker .react-datepicker__year-option:hover {
            background: rgba(129,140,248,0.18) !important;
          }
        `}
      </style>
    <div className="max-w-8xl mx-auto md:mt-20 md:mb-20">
      <div className="
            relative overflow-visible
            rounded-[20px] p-8 md:p-10
            bg-gradient-to-br from-[#1e174a] to-[#0f0c29]
            backdrop-blur-2xl text-white
            border border-white/10
            shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]
          ">
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 px-2 text-white">
          <div className="flex items-center gap-4">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-xl shadow-indigo-500/20 md:mt-0 mt-[-24px]">
              <LayoutList className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-white whitespace-nowrap">Leave Reports</h1>
              <p className="text-white/60 text-sm">Analyze and overview the leave history of your entire team</p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <Calendar className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45" />
              <DatePicker
                selected={selectedMonth}
                onChange={handleMonthChange}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                showFullMonthYearPicker
                calendarClassName="leave-report-datepicker"
                popperClassName="z-[9999]"
                className="h-12 w-full rounded-2xl border border-white/10 bg-white/5 pl-11 pr-4 text-sm font-bold text-white outline-none transition focus:border-indigo-400/50 md:w-[180px]"
              />
            </div>
            <button
              onClick={handleExportExcel}
              className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 px-4 text-xs font-black uppercase tracking-widest text-emerald-100 transition hover:bg-emerald-400/18"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>
            <button
              onClick={handleExportPdf}
              className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-rose-300/20 bg-rose-400/10 px-4 text-xs font-black uppercase tracking-widest text-rose-100 transition hover:bg-rose-400/18"
            >
              <FileText className="h-4 w-4" />
              PDF
            </button>
            <button
              onClick={handlePrintReport}
              className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-sky-300/20 bg-sky-400/10 px-4 text-xs font-black uppercase tracking-widest text-sky-100 transition hover:bg-sky-400/18"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-2xl transition-all duration-300 font-bold text-xs uppercase tracking-widest ${showFilters ? 'bg-white text-black' : 'bg-white/5 border border-white/10 text-white'}`}
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            {/* <button className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 transition-all shadow-xl shadow-blue-500/20 font-bold text-xs uppercase tracking-widest">
            <Download className="w-4 h-4" />
            Export Data
          </button> */}
          </div>
        </div>
        {/* FILTER DRAWER */}
        {showFilters && (
          <div className="relative z-[80] mb-4 mt-[-14px] p-4 rounded-lg bg-white/5 border border-white/10 backdrop-blur-3xl animate-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-2 md:gap-4">
              {/* <FilterGroup label="Search Employee" icon={<Search className="w-3.5 h-3.5" />}> */}
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
                <input
                  name="search"
                  placeholder="Search Employee..."
                  onChange={handleChange}
                  value={filters.search}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-1 pl-10 pr-5 text-sm text-white placeholder-white/60 outline-none focus:border-indigo-500/50"
                />
              </div>
              {/* </FilterGroup> */}
              <ReportSelect
                value={filters.leave_type}
                options={[
                  { value: "", label: "Leave Type" },
                  { value: "Casual", label: "Casual" },
                  { value: "Unpaid Leave", label: "Leave Without Pay [LWP]" },
                  { value: "Half Day", label: "Half Day" },
                  { value: "Short Leave", label: "Short Leave" },
                  { value: "Work From Home", label: "Work From Home" },
                ]}
                onChange={(value) => setFilters((current) => ({ ...current, leave_type: value }))}
              />

              <ReportSelect
                value={filters.status}
                options={[
                  { value: "", label: "All Status" },
                  { value: "1", label: "Approved" },
                  { value: "0", label: "Rejected" },
                  { value: "pending", label: "Pending" },
                ]}
                onChange={(value) => setFilters((current) => ({ ...current, status: value }))}
              />
              {/* <FilterGroup label="From Date" icon={<Calendar className="w-3.5 h-3.5" />}> */}
              <DatePicker
                selected={filters.start_date ? new Date(filters.start_date) : null}
                onChange={(date) =>
                  setFilters({
                    ...filters,
                    start_date: date ? date.toISOString().split("T")[0] : "",
                  })
                }
                dateFormat="yyyy-MM-dd"
                dateFormatCalendar=" "
                // className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
                calendarClassName="leave-report-datepicker"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={6}
                placeholderText="Start Date"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-5 py-1 text-sm text-white placeholder-white/20 outline-none focus:border-indigo-500/50"

                portalId="root"        // ✅ KEY FIX
                popperPlacement="bottom-start"
                popperClassName="z-[9999]"   // ✅ ensure on top
              />
              {/* </FilterGroup> */}
              {/* <FilterGroup label="To Date" icon={<Calendar className="w-3.5 h-3.5" />}> */}
              <DatePicker
                selected={filters.end_date ? new Date(filters.end_date) : null}
                onChange={(date) =>
                  setFilters({
                    ...filters,
                    end_date: date ? date.toISOString().split("T")[0] : "",
                  })
                }
                dateFormat="yyyy-MM-dd"
                dateFormatCalendar=" "
                // className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
                calendarClassName="leave-report-datepicker"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={6}
                placeholderText="End Date"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-5 py-1 text-sm text-white placeholder-white/20 outline-none focus:border-indigo-500/50"

                portalId="root"        // ✅ KEY FIX
                popperPlacement="bottom-start"
                popperClassName="z-[9999]"   // ✅ ensure on top
              />
              {/* </FilterGroup> */}
            </div>
            <div className=" mt-2 border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[10px] font-bold text-white/35 uppercase tracking-widest flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                {formatMonth(selectedMonth)} • {groupedReport.length} employees • {filteredReport.length} records
              </p>
              <div className="flex gap-4 w-full sm:w-auto">
                <button
                  onClick={handleReset}
                  className="flex-1 sm:flex-none whitespace-nowrap py-1.5 flex items-center justify-center gap-2 md:gap-1 px-2 md:px-10 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white transition-all text-sm font-bold"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset Filters
                </button>
                <button
                  onClick={loadReport}
                  className="flex-1 sm:flex-none px-3 py-1 md:px-10 md:py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white transition-all shadow-lg shadow-indigo-500/20 text-xs font-black uppercase tracking-widest"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
        {/* AUDIT DATA TABLE */}
        <div className="relative z-0 overflow-hidden rounded-lg bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-white border-collapse">
              <thead>
                <tr className="bg-white/[0.03] border-b border-white/10">
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Employee </th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Leave Summary</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Date Range</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Total</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Latest Reason</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Latest Remarks</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Decision</th>
                  {/* <th className="p-6 text-[10px] font-black uppercase tracking-[2px] text-white/30 text-center whitespace-nowrap">Audit</th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 whitespace-nowrap">
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="p-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                        <span className="text-xs font-bold text-white/30 uppercase tracking-widest">Crunching data...</span>
                      </div>
                    </td>
                  </tr>
                ) : groupedReport.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-20 text-center">
                      <div className="flex flex-col items-center gap-4 opacity-10">
                        <FileSearch className="w-20 h-20" />
                        <span className="text-sm font-black uppercase tracking-widest">No audit trails matched your criteria</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  groupedReport.map((group) => {
                    const latest = group.entries[0];
                    const expanded = expandedEmployees[group.key];
                    const hasMultiple = group.entries.length > 1;
                    const startDates = group.entries.map((entry) => entry.leave_start_date).sort();
                    const endDates = group.entries.map((entry) => entry.leave_end_date).sort();
                    const categoryCount = group.entries.reduce((counts, entry) => {
                      const label = entry.leave_type === "Unpaid Leave" ? "LWP" : entry.leave_type;
                      counts[label] = (counts[label] || 0) + 1;
                      return counts;
                    }, {});

                    return (
                      <Fragment key={group.key}>
                        <tr className="group hover:bg-white/[0.04] transition-all duration-300">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={!hasMultiple}
                                onClick={() => hasMultiple && toggleEmployee(group.key)}
                                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border transition-all ${
                                  hasMultiple
                                    ? "border-indigo-300/25 bg-indigo-400/10 text-indigo-100 hover:bg-indigo-400/20"
                                    : "border-white/10 bg-white/5 text-white/20"
                                }`}
                                title={hasMultiple ? "Show leave entries" : "Single leave entry"}
                              >
                                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => navigate(`/users/${group.employee_id}`)}
                                className="flex items-center gap-3 text-white/80 hover:text-indigo-400 transition-colors text-left"
                              >
                                <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                                  <User className="w-3 h-3" />
                                </div>
                                <div>
                                  <p className="text-sm text-white/70 font-semibold tracking-tight">{group.employee_name}</p>
                                  <p className="text-[10px] font-bold text-white/30 uppercase">
                                    {group.entries.length} {group.entries.length === 1 ? "entry" : "entries"}
                                  </p>
                                </div>
                              </button>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(categoryCount).map(([label, count]) => (
                                <span key={label} className="rounded-lg bg-white/[0.06] px-2 py-1 text-[11px] font-bold text-white/65">
                                  {label} {count > 1 ? `x${count}` : ""}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">
                            <p className="text-xs font-medium text-white/60">
                              {formatDate(startDates[0])} - {formatDate(endDates[endDates.length - 1])}
                            </p>
                          </td>
                          <td className="p-3 text-center">
                            <div className="inline-flex items-baseline gap-1 bg-white/5 px-3 py-1 rounded-lg">
                              <span className="text-sm font-semibold">{group.totalDuration}</span>
                              <span className="text-[9px] font-bold text-white/30 uppercase">Days</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <p className="text-xs text-white/60 italic max-w-[220px] truncate" title={latest.employee_comment}>
                              {latest.employee_comment || "-"}
                            </p>
                          </td>
                          <td className="p-3">
                            {latest.remarks ? (
                              <p className="text-xs text-amber-300/80 italic max-w-[220px] truncate" title={latest.remarks}>
                                {latest.remarks}
                              </p>
                            ) : (
                              <span className="text-white/20 text-xs">—</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {getStatusBadge(latest.approval_status)}
                          </td>
                        </tr>
                        {hasMultiple && (
                          <tr className="bg-black/10">
                            <td colSpan="7" className="p-0">
                              <div className={`overflow-hidden transition-all duration-500 ease-out ${expanded ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"}`}>
                                <div className="m-3 rounded-2xl border border-white/10 bg-white/[0.035] p-3">
                                  <div className="mb-3 flex items-center justify-between gap-3">
                                    <p className="text-xs font-black uppercase tracking-[0.18em] text-white/45">
                                      {group.employee_name}'s {formatMonth(selectedMonth)} leave entries
                                    </p>
                                    <span className="rounded-full bg-indigo-400/12 px-3 py-1 text-[11px] font-bold text-indigo-100">
                                      {group.entries.length} records
                                    </span>
                                  </div>
                                  <div className="grid gap-2">
                                    {group.entries.map((entry) => (
                                      <div key={entry.id} className="grid grid-cols-1 gap-3 rounded-xl border border-white/[0.08] bg-[#120d2c]/70 p-3 md:grid-cols-[1.1fr_1.1fr_0.7fr_1.6fr_1.2fr_0.8fr] md:items-center">
                                        <div>
                                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Category</p>
                                          <p className="text-xs font-bold text-white/75">{entry.leave_type === "Unpaid Leave" ? "Leave Without Pay [LWP]" : entry.leave_type}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Period</p>
                                          <p className="text-xs font-medium text-white/65">{formatDate(entry.leave_start_date)} - {formatDate(entry.leave_end_date)}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Duration</p>
                                          <p className="text-xs font-bold text-white/75">{entry.duration} days</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Reason</p>
                                          <p className="text-xs italic text-white/60 whitespace-normal">{entry.employee_comment || "-"}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Remarks</p>
                                          <p className="text-xs italic text-amber-300/75 whitespace-normal">{entry.remarks || "-"}</p>
                                        </div>
                                        <div>{getStatusBadge(entry.approval_status)}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
function ReportSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={menuRef} className="relative z-[90]">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-1 text-left text-sm transition ${
          open
            ? "border-indigo-300/50 bg-indigo-400/12 text-white ring-2 ring-indigo-300/15"
            : "border-white/10 bg-white/5 text-white/78 hover:border-white/20 hover:bg-white/[0.08]"
        }`}
      >
        <span className="truncate">{selected.label}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-white/45 transition ${open ? "rotate-180 text-indigo-100" : ""}`} />
      </button>

      {open && (
        <>
          <div className="absolute left-0 top-[calc(100%+8px)] z-[100] w-full min-w-[230px] overflow-hidden rounded-2xl border border-white/12 bg-[#130d2f] p-1.5 text-white shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
            {options.map((option) => {
              const active = option.value === value;

              return (
                <button
                  key={option.value || "all"}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                    active
                      ? "bg-indigo-400/16 text-indigo-50"
                      : "text-white/72 hover:bg-white/[0.08] hover:text-white"
                  }`}
                >
                  <span className="truncate font-semibold">{option.label}</span>
                  {active && <span className="h-2 w-2 rounded-full bg-indigo-200 shadow-[0_0_14px_rgba(199,210,254,0.75)]" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}



// import { useState, useEffect } from "react";
// import { adminLeaveReportApi } from "../../app/api";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";

// /* DATE FORMATTER */
// const formatDate = (date) => {
//   if (!date) return "-";
//   return new Date(date).toLocaleDateString("en-GB", {
//     day: "2-digit",
//     month: "short",
//     year: "numeric",
//   });
// };
// export default function LeaveReport() {
//   const navigate = useNavigate();
//   const [filters, setFilters] = useState({
//     status: "",
//     start_date: "",
//     end_date: "",
//     leave_type: "",
//     search: "",
//   });
//   const [report, setReport] = useState([]);
//   const handleChange = (e) => {
//     setFilters({ ...filters, [e.target.name]: e.target.value });
//   };

//   const handleReset = () => {
//     setFilters({
//       status: "",
//       start_date: "",
//       end_date: "",
//       leave_type: "",
//       search: "",
//     });
//   };
//   // const loadReport = async () => {
//   //   const res = await adminLeaveReportApi(filters);
//   //   if (res.success) {
//   //     setReport(res.data || []);
//   //   } else {
//   //     toast.error("Failed to load report");
//   //   }
//   // };

//   const loadReport = async () => {
//     const res = await adminLeaveReportApi();
//     if (res.success) {
//       setReport(res.data || []);
//     } else {
//       toast.error("Failed to load report");
//     }
//   };
//   useEffect(() => {
//     loadReport();
//   }, []);


//   const filteredReport = report.filter((r) => {
//     const matchesSearch =
//       filters.search === "" ||
//       r.employee_name
//         ?.toLowerCase()
//         .includes(filters.search.toLowerCase());
//     const matchesLeaveType =
//       filters.leave_type === "" ||
//       r.leave_type === filters.leave_type;
//     const matchesStatus =
//       filters.status === "" ||
//       Number(r.approval_status) === Number(filters.status);
//     const matchesStartDate =
//       filters.start_date === "" ||
//       r.leave_start_date >= filters.start_date;
//     const matchesEndDate =
//       filters.end_date === "" ||
//       r.leave_end_date <= filters.end_date;
//     return (
//       matchesSearch &&
//       matchesLeaveType &&
//       matchesStatus &&
//       matchesStartDate &&
//       matchesEndDate
//     );
//   });
//   /* STATUS BADGE */
//   const getStatusBadge = (status) => {
//     if (status === 1)
//       return (
//         <span className="px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-300">
//           Approved
//         </span>
//       );
//     if (status === 0)
//       return (
//         <span className="px-3 py-1 rounded-full text-xs bg-red-500/20 text-red-300">
//           Rejected
//         </span>
//       );
//     return (
//       <span className="px-3 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-300">
//         Pending
//       </span>
//     );
//   };





//   return (
//     <div className="rounded-2xl p-6 md:mt-20 bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f] text-white shadow-2xl">
//       <h2 className="font-bold text-2xl mb-6">
//         Leave Report (Admin)
//       </h2>
//       {/* FILTERS */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6 text-sm">
//         <input
//           name="search"
//           placeholder="Search employee"
//           onChange={handleChange}
//           value={filters.search}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
//         />
//         <select
//           name="leave_type"
//           onChange={handleChange}
//           value={filters.leave_type}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         >
//           <option value="">Leave Type</option>
//           <option value="Casual Leave">Casual Leave</option>
//           <option value="Half Day">Half Day</option>
//           <option value="Short Leave">Short Leave</option>
//           <option value="Work From Home">Work From Home</option>
//         </select>
//         <select
//           name="status"
//           onChange={handleChange}
//           value={filters.status}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         >
//           <option value="">All Status</option>
//           <option value="1">Approved</option>
//           <option value="0">Rejected</option>
//         </select>
//         {/* <input
//           type="date"
//           name="start_date"
//           onChange={handleChange}
//           value={filters.start_date}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         />
//         <input
//           type="date"
//           name="end_date"
//           onChange={handleChange}
//           value={filters.end_date}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         /> */}

//         {/* START DATE */}
//         {/* <DatePicker
//   selected={filters.start_date ? new Date(filters.start_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       start_date: date
//         ? date.toISOString().split("T")[0]
//         : "",
//     })
//   }
//   placeholderText="Start Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// /> */}
// <DatePicker
//   selected={filters.start_date ? new Date(filters.start_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       start_date: date ? date.toISOString().split("T")[0] : "",
//     })
//   }
//   dateFormat="yyyy-MM-dd"
//   dateFormatCalendar=" "
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
//   calendarClassName="custom-calendar"
//   showMonthDropdown
//   showYearDropdown
//   scrollableYearDropdown
//   yearDropdownItemNumber={6}
//   placeholderText="Start Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// />
//         {/* END DATE */}
//         {/* <DatePicker
//   selected={filters.end_date ? new Date(filters.end_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       end_date: date
//         ? date.toISOString().split("T")[0]
//         : "",
//     })
//   }
//   placeholderText="End Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// /> */}
// <DatePicker
//   selected={filters.end_date ? new Date(filters.end_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       end_date: date ? date.toISOString().split("T")[0] : "",
//     })
//   }
//   dateFormat="yyyy-MM-dd"
//   dateFormatCalendar=" "
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
//   calendarClassName="custom-calendar"
//   showMonthDropdown
//   showYearDropdown
//   scrollableYearDropdown
//   yearDropdownItemNumber={6}
//   placeholderText="End Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// />

//       </div>
//       <div className="flex gap-3 mb-6">
//         <button
//           onClick={loadReport}
//           className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm shadow-lg"
//         >
//           Filter Report
//         </button>
//         <button
//           onClick={handleReset}
//           className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm shadow-lg"
//         >
//           Reset
//         </button>
//       </div>
//       {/* ================= DESKTOP TABLE ================= */}
//       <div className="hidden md:block overflow-x-auto">
//         <table className="min-w-[900px] w-full text-sm">
//           <thead className="text-white/70 border-b border-white/10">
//             <tr>
//               <th className="p-3 text-left">Employee</th>
//               <th className="p-3 text-left">Leave Type</th>
//               <th className="p-3 text-left">Start</th>
//               <th className="p-3 text-left">End</th>
//               <th className="p-3">Days</th>
//               <th className="p-3">Status</th>
//             </tr>
//           </thead>
//           <tbody>
//             {/* {report.length === 0 ? ( */}
//             {filteredReport.length === 0 ? (
//               <tr>
//                 <td colSpan="6" className="py-8 text-center opacity-60">
//                   No records found
//                 </td>
//               </tr>
//             ) : (
//               // report.map((r) => (
//               filteredReport.map((r) => (
//                 <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
//                   <td className="p-3 font-medium">
//                     <button
//                       onClick={() => navigate(`/users/${r.employee_id}`)}
//                       // onClick={() => navigate(`/users/${leave.employee_id}`)}
//                       className="text-blue-300 hover:underline"
//                     >
//                       {r.employee_name}
//                     </button>
//                   </td>
//                   <td className="p-3">
//                     {r.leave_type}
//                   </td>
//                   <td className="p-3">
//                     {formatDate(r.leave_start_date)}
//                   </td>
//                   <td className="p-3">
//                     {formatDate(r.leave_end_date)}
//                   </td>
//                   <td className="p-3 text-center">
//                     {r.duration}
//                   </td>
//                   <td className="p-3 text-center">
//                     {getStatusBadge(r.approval_status)}
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>
//       {/* ================= MOBILE CARDS ================= */}
//       <div className="md:hidden space-y-3">
//         {filteredReport.map((r) => (
//           // {/* filteredReport.map((r) => ( */}
//           <div
//             key={r.id}
//             className="bg-gradient-to-br from-[#2d1c6a] to-[#12082f] border border-white/10 rounded-2xl p-4 shadow-lg"
//           >
//             <div className="flex justify-between mb-2">
//               <p
//                 className="font-semibold text-blue-300 cursor-pointer"
//                 onClick={() => navigate(`/users/${r.employee_id}`)}
//               // employee_id
//               >
//                 {r.employee_name}
//               </p>
//               {getStatusBadge(r.approval_status)}
//             </div>
//             <div className="grid grid-cols-2 gap-3 text-sm">
//               <div>
//                 <p className="text-white/60 text-xs">Leave Type</p>
//                 <p>{r.leave_type}</p>
//               </div>
//               <div>
//                 <p className="text-white/60 text-xs">Days</p>
//                 <p className="font-semibold text-blue-300">
//                   {r.duration}
//                 </p>
//               </div>
//               <div>
//                 <p className="text-white/60 text-xs">Start</p>
//                 <p>{formatDate(r.leave_start_date)}</p>
//               </div>
//               <div>
//                 <p className="text-white/60 text-xs">End</p>
//                 <p>{formatDate(r.leave_end_date)}</p>
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }
